import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { ARCHITECTURE_LAYERS, IMAGES } from "@/data/content";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { Reveal } from "@/components/shared/Reveal";
import { Icon } from "@/components/shared/Icon";

export const Architecture = () => (
  <section data-testid="architecture-section" className="section relative overflow-hidden">
    <div className="absolute inset-y-0 right-0 -z-10 w-1/2 opacity-[0.12]">
      <img src={IMAGES.controlRoom} alt="" className="h-full w-full object-cover" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/60 to-navy/20" />
    </div>
    <div className="wrap grid gap-14 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <div className="lg:sticky lg:top-32">
          <SectionHeading
            eyebrow="From vehicle to control room"
            title="From vehicle to control room, we connect the entire transportation ecosystem."
            text="Five layers, one architecture. Data flows from the vehicle through connectivity into intelligence, enterprise systems and finally the people who run the operation."
          />
          <Reveal delay={0.2} className="mt-10 hidden items-center gap-3 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 lg:flex">
            <span className="h-px w-10 bg-cyan-400" /> Data direction: vehicle → operations
          </Reveal>
        </div>
      </div>

      <div className="relative lg:col-span-7">
        <div className="absolute left-6 top-6 bottom-6 w-px bg-line-strong lg:left-8">
          <motion.span
            className="absolute left-1/2 h-24 w-[3px] -translate-x-1/2 rounded-full bg-gradient-to-b from-transparent via-cyan-400 to-transparent"
            animate={{ top: ["-10%", "100%"] }}
            transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
          />
        </div>
        <div className="space-y-4">
          {ARCHITECTURE_LAYERS.map((layer, i) => (
            <div key={layer.id}>
              <Reveal delay={i * 0.05} data-testid={`architecture-layer-${layer.id}`} className="card group ml-14 p-6 lg:ml-20 lg:p-8">
                <span className="absolute -left-14 top-7 flex h-10 w-10 items-center justify-center rounded-full border border-line-strong bg-navy text-cyan-400 transition-[box-shadow] duration-300 group-hover:glow-dot lg:-left-[4.75rem] lg:top-8 lg:h-12 lg:w-12">
                  <Icon name={layer.icon} className="h-5 w-5" />
                </span>
                <div className="flex items-baseline justify-between gap-4">
                  <h3 className="text-xl font-semibold">{layer.name}</h3>
                  <span className="font-mono text-xs text-slate-500">L{i + 1}</span>
                </div>
                <div className="mt-5 flex flex-wrap gap-2">
                  {layer.items.map((it) => (
                    <span key={it} className="rounded-md border border-line bg-navy px-3 py-1.5 text-xs font-medium text-slate-300 transition-colors group-hover:border-line-strong">{it}</span>
                  ))}
                </div>
              </Reveal>
              {i < ARCHITECTURE_LAYERS.length - 1 && (
                <div className="ml-14 flex justify-center py-1 text-slate-600 lg:ml-20"><ArrowDown className="h-4 w-4" /></div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);
